import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import axios from 'axios';
import { DEFAULT_BRANDING } from '../../context/BrandingContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7123';
const DEFAULT_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  'app.stackbd.com',
  'dev-app.stackbd.com',
]);

/**
 * REQ-21..REQ-23 — Custom login page replacing Cognito Hosted UI.
 *
 * Detects the hostname (or the `window.__WL_FORCE_DOMAIN__` test override),
 * fetches the tenant's branding via `/v1/branding/by-domain`, renders a
 * branded login surface, and initiates the OIDC authorization code flow.
 *
 * Default hostnames fall back to Stack BD branding (REQ-23).
 */
const CustomLoginPage = () => {
  const auth = useAuth();
  const [branding, setBranding] = useState({ ...DEFAULT_BRANDING });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const host = (typeof window !== 'undefined' && (window.__WL_FORCE_DOMAIN__ || window.location.hostname)) || '';
    const isCustom = host && !DEFAULT_HOSTS.has(host);
    if (!isCustom) {
      setIsLoading(false);
      return;
    }
    axios
      .get(`${API_BASE}/v1/branding/by-domain`, { params: { domain: host } })
      .then((res) => {
        setBranding({ ...DEFAULT_BRANDING, ...res.data });
      })
      .catch(() => {
        // Domain not registered — fall through to default branding
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleSignIn = () => {
    auth.signinRedirect();
  };

  return (
    <div className="custom-login-page" data-testid="custom-login-page">
      <div className="custom-login-card">
        {branding.logo_url ? (
          <img
            src={branding.logo_url}
            alt={branding.app_name}
            data-testid="login-logo"
            className="custom-login-logo"
          />
        ) : (
          <img
            src="/logo192.png"
            alt={branding.app_name}
            data-testid="login-logo"
            className="custom-login-logo"
          />
        )}
        <h1 data-testid="login-app-name">{branding.app_name}</h1>
        <p>Sign in to continue</p>
        <button
          type="button"
          data-testid="login-signin-btn"
          className="custom-login-btn"
          onClick={handleSignIn}
          disabled={isLoading}
          style={{
            backgroundColor: branding.primary_color,
            color: '#fff',
          }}
        >
          Sign in
        </button>
        {branding.terms_url && branding.privacy_url && (
          <div className="custom-login-legal">
            <a href={branding.terms_url} target="_blank" rel="noreferrer">Terms</a>
            <span> · </span>
            <a href={branding.privacy_url} target="_blank" rel="noreferrer">Privacy</a>
          </div>
        )}
        {branding.support_email && (
          <p className="custom-login-support">
            Need help? <a href={`mailto:${branding.support_email}`}>{branding.support_email}</a>
          </p>
        )}
      </div>
    </div>
  );
};

export default CustomLoginPage;
